import React, { useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { CircleNotch, Coins, ArrowsClockwise } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { getVariant, track, trackImpressionOnce } from "../lib/experiment";
import { isIOS } from "../lib/platform";
import * as StoreKitDirect from "../lib/storekitDirect";
import PaywallIncludes from "./paywall/PaywallIncludes";
import PaywallTrialBanner from "./paywall/PaywallTrialBanner";
import PaywallEarlySupporterHero from "./paywall/PaywallEarlySupporterHero";
import PaywallEarlySupporterSoldOut from "./paywall/PaywallEarlySupporterSoldOut";
import PaywallPriceCard from "./paywall/PaywallPriceCard";
import PaywallIOSPriceCard from "./paywall/PaywallIOSPriceCard";
import PaywallIOSDisclosure from "./paywall/PaywallIOSDisclosure";
import { usePaywallPurchase } from "./paywall/usePaywallPurchase";
import { APP_REVIEW_LOCKDOWN } from "../lib/appReview";

/**
 * Paywall sheet — coordinates pricing, trial, A/B testing.
 *
 * T17 / Build 60 — RC paywall-open path fully removed. iOS uses
 * StoreKitDirect ONLY for product fetch + purchase on tap. There is no
 * paywall-open RC fetch, no watchdog, no diagnostic card, no silent
 * re-fetch loop. The static fallback cards are now the canonical iOS
 * paywall surface.
 */
export default function Paywall({ open, onClose, reason }) {
  const { user, signIn, startTrial, refresh } = useAuth();
  const [prices, setPrices] = useState([]);
  const [iosOfferings, setIosOfferings] = useState([]);
  const [iosError, setIosError] = useState(null);
  const [iosLoading, setIosLoading] = useState(false);
  const [iosFetchNonce, setIosFetchNonce] = useState(0);
  const [staticBusy, setStaticBusy] = useState(null); // "yearly" | "monthly" | null
  const [purchaseStep, setPurchaseStep] = useState(null); // current step label, e.g. "Configuring SDK"
  const [purchaseError, setPurchaseError] = useState(null); // error msg from failed purchase
  const [earlyStatus, setEarlyStatus] = useState(null);

  // T44 — Diagnostic helper. The previous TestFlight detection used a
  // regex on navigator.userAgent which has no "TestFlight" token in the
  // iOS WKWebView, so we never actually surfaced raw codes during
  // Build 65 testing. We now always show the full diagnostic when
  // running inside Capacitor on iOS (TestFlight, dev, and App Store
  // builds during the Apple Review iteration). Once the paywall is
  // verified green on App Review we can flip this to a build-time env
  // var. For now, transparent error reporting wins.
  function _fmtPaywallError(step, productId, err) {
    const code = err?.code || "UNKNOWN";
    const msg = err?.message || String(err);
    const data = err?.data || {};
    // Compact, screenshot-friendly multiline:
    //   [step] productId=tiplingo_pro_yearly
    //   CODE — message
    //   {"requestedIds":["…"], "returnedCount":0, "bundleId":"com.tiplingo.app"}
    const dataStr = Object.keys(data).length ? JSON.stringify(data) : "";
    return `[${step}] productId=${productId}\n${code} — ${msg}${dataStr ? "\n" + dataStr : ""}`;
  }

  async function handleStaticPurchase(productId, slot) {
    if (staticBusy) return;
    setStaticBusy(slot);
    setPurchaseStep("Preparing purchase");
    setPurchaseError(null);

    // T16 / Build 58 — iOS purchase path uses the native StoreKit 2
    // Capacitor plugin EXCLUSIVELY. RevenueCat is fully bypassed.
    //
    // T43 — `Capacitor.isPluginAvailable()` synchronous preflight
    // removed; `ping()` is the canonical runtime probe.
    //
    // T44 — Each step (ping / getProduct / purchase) now reports a
    // multi-line diagnostic on failure: step, productId, code,
    // message, and any structured data the native side returned
    // (e.g. requestedIds, returnedCount, bundleId).
    //
    // T49.X (Build 83) — Full entitlement reconcile after EVERY
    // terminal state (success, pending, and even user-cancelled).
    // The old code only POSTed the just-returned single transaction
    // to /iap/apple/apply; if that one POST hit any transient hiccup
    // the yearly buy would look "purchased" on Apple but stay "Free"
    // in the app until the user tapped a second product and Apple's
    // "already subscribed" flow forced a fuller reconcile. Now we
    // ALSO call syncAppleEntitlement() which walks
    // Transaction.currentEntitlements on the device and posts the
    // full truth — self-healing regardless of which single tx was
    // involved. Also run it after USER_CANCELLED so dismissing the
    // "You're currently subscribed to this" modal STILL flips the
    // UI to Pro.
    async function reconcileEntitlement(context) {
      try {
        setPurchaseStep("Syncing subscription…");
        const { syncAppleEntitlement } = await import("../lib/iapAutoSync");
        await syncAppleEntitlement();
      } catch (syncErr) {
        console.warn(`[Paywall] T49.X ${context} full sync failed:`, syncErr?.message || syncErr);
      }
    }
    try {
      // ─── Step 1: ping ───────────────────────────────────────────
      try {
        const pong = await StoreKitDirect.ping();
        console.info("[Paywall] T44 ping:", pong);
        if (!pong?.ok) {
          console.warn("[Paywall] T44 ping returned non-ok payload:", pong);
        }
      } catch (pingErr) {
        const dbg = _fmtPaywallError("ping", productId, pingErr);
        console.error("[Paywall] T44", dbg);
        setPurchaseStep(`Error: ${dbg}`);
        setPurchaseError(dbg);
        return;
      }

      // ─── Step 2: getProduct ─────────────────────────────────────
      setPurchaseStep(`Fetching StoreKit product — ${productId}`);
      try {
        const product = await StoreKitDirect.getProduct(productId);
        console.info("[Paywall] T44 getProduct:", product);
      } catch (fetchErr) {
        const dbg = _fmtPaywallError("getProduct", productId, fetchErr);
        console.error("[Paywall] T44", dbg);
        setPurchaseStep(`Error: ${dbg}`);
        setPurchaseError(dbg);
        return;
      }

      // ─── Step 3: purchase ───────────────────────────────────────
      setPurchaseStep("Opening App Store purchase…");
      const result = await StoreKitDirect.purchase(productId);

      if (result?.pending) {
        setPurchaseStep("Purchase pending Apple approval — you'll be unlocked when it clears");
        // Even on pending, some entitlements may already exist (upgrade
        // from a lower-tier sub, etc.). Reconcile so UI reflects reality.
        await reconcileEntitlement("pending");
      } else {
        setPurchaseStep("Purchase complete — syncing entitlement");
        // T47 — POST the native StoreKit transaction directly to the
        // backend's Apple-apply endpoint. Bypasses RC (which has zero
        // record of StoreKitDirect purchases) so the user's plan
        // flips to Pro the instant Apple confirms the sheet.
        try {
          const { applyTransactions } = await import("../lib/iapAutoSync");
          await applyTransactions([{
            productId: result?.productId || productId,
            transactionId: result?.transactionId,
            originalTransactionId: result?.originalTransactionId,
            purchaseDate: result?.purchaseDate,
            expirationDate: result?.expirationDate,
          }]);
        } catch (_e) { /* refresh below picks up RC webhook fallback */ }
        // T49.X — Full reconcile is the belt-and-suspenders for the
        // "yearly still shows Free Plan" bug. Walks every active
        // entitlement, not just this one transaction. Idempotent
        // server-side.
        await reconcileEntitlement("success");
      }
      await refresh();
      onClose?.();
    } catch (e) {
      const code = e?.code || "";
      // T44 — USER_CANCELLED is the only error we silence; everything
      // else gets the structured banner so the user can screenshot
      // and we can debug from a one-frame video.
      //
      // T49.X (Build 83) — On USER_CANCELLED we ALSO reconcile.
      // Apple's "You're currently subscribed to this" modal returns
      // .userCancelled to StoreKit even though the user is actually
      // entitled. Silently walking currentEntitlements after cancel
      // upgrades those users from Free → Pro without a second tap.
      // If they truly cancelled with no entitlement, the reconcile
      // is a no-op (0 transactions returned).
      if (code === "USER_CANCELLED" || /cancel/i.test(e?.message || "")) {
        setPurchaseStep(null);
        try {
          const { syncAppleEntitlement } = await import("../lib/iapAutoSync");
          const synced = await syncAppleEntitlement();
          if (synced?.active) {
            await refresh();
            const { toast } = await import("sonner");
            toast.success("Welcome to Pro! 🎉");
            onClose?.();
          }
        } catch (_e) { /* silent — cancel path stays quiet */ }
        return;
      }
      const dbg = _fmtPaywallError("purchase", productId, e);
      console.error("[Paywall] T44", dbg);
      setPurchaseError(dbg);
      setPurchaseStep(`Error: ${dbg}`);
      try {
        const { toast } = await import("sonner");
        toast.error(`${code || "purchase"} — ${e?.message || ""}`);
      } catch (_e) { /* sonner optional */ }
    } finally {
      setStaticBusy(null);
    }
  }
  const ios = isIOS();

  // Sticky A/B variant for the paywall layout. A = control, B = early-supporter hero.
  const variant = useMemo(() => getVariant("paywall_v1", ["A", "B"]), []);

  const { busy, buyIOS, buy, onRestore, onStartTrial } = usePaywallPurchase({
    user,
    signIn,
    startTrial,
    refresh,
    onClose,
    ios,
    variant,
  });

  useEffect(() => {
    if (!open) return;
    if (ios) {
      // T17 / Build 60 — RC paywall-open path REMOVED. The tap path uses
      // StoreKitDirect exclusively (see handleStaticPurchase). We no
      // longer call any RC SDK method on paywall open. This eliminates
      // the App-Review-visible stale "configure timed out / offerings
      // watchdog" error path entirely.
      const PAYWALL_CODE_VERSION = "2026-06-20-T17";
      console.info(
        `[Paywall] iOS detected — RC paywall-open path disabled (code=${PAYWALL_CODE_VERSION})`,
      );
      // Static fallback cards are always rendered for iOS; no fetching needed.
      setIosError(null);
      setIosLoading(false);
    } else if (APP_REVIEW_LOCKDOWN) {
      // Build 84 web lockdown — DO NOT fetch /payments/prices on web
      // during App Review. Backend `sub_yearly_early` package carries
      // the "TipLingo Pro First Year" label + $19.99 amount which Apple
      // flagged. Under lockdown the web paywall renders a static safe
      // layout (Option A) — Monthly $4.99 + Yearly $39.99 with buttons
      // labeled "Available in the iOS app". No dynamic backend copy
      // reaches the DOM, so the runtime scrubber has nothing to fire on
      // and there is no blank-page failure mode. Restored post-approval
      // by removing this branch.
      setPrices([]);
      setEarlyStatus(null);
    } else {
      api.get("/payments/prices").then((r) => setPrices(r.data.items || []));
      // Live early-supporter cap status (web only — gated by `ios` above).
      api
        .get("/payments/early-supporter/status")
        .then((r) => setEarlyStatus(r.data))
        .catch(() => setEarlyStatus(null));
    }
    trackImpressionOnce("paywall_v1", variant, { reason: reason || null, platform: ios ? "ios" : "web" });
    track("paywall_open", { reason: reason || null, plan: user?.plan || "anon", platform: ios ? "ios" : "web" }, variant);
  }, [open, reason, variant, user?.plan, ios, iosFetchNonce]);

  const subsRaw = prices.filter((p) => p.kind === "subscription");
  // Hide the Early Supporter SKU from the regular subs list once the launch
  // promo has reached its cap. Falls back to "available" when status hasn't
  // loaded yet so we don't briefly flash the wrong layout.
  const earlySoldOut = earlyStatus && earlyStatus.available === false;
  const subs = earlySoldOut
    ? subsRaw.filter((p) => p.id !== "sub_yearly_early")
    : subsRaw;
  const credits = prices.filter((p) => p.kind === "credits");
  const onPro = user?.plan === "pro";

  // iOS — sort so the yearly "Best Value" card always renders first
  // (per Apple 3.1.2(c) UX guidance: best-value plan first, monthly
  // remains clearly available below).
  const sortedIosOfferings = useMemo(() => {
    return [...iosOfferings].sort((a, b) => {
      if (a.period === "year" && b.period !== "year") return -1;
      if (a.period !== "year" && b.period === "year") return 1;
      return 0;
    });
  }, [iosOfferings]);

  // iOS — compute the "Save $X compared to monthly" badge dynamically
  // from the live RevenueCat price strings so the badge stays accurate
  // if you ever change prices in App Store Connect. Renders only when
  // both monthly + yearly are present and yearly is genuinely cheaper.
  const iosYearlySavingsLabel = useMemo(() => {
    const monthly = sortedIosOfferings.find((p) => p.period === "month");
    const yearly = sortedIosOfferings.find((p) => p.period === "year");
    if (!monthly || !yearly) return null;
    const parse = (s) => parseFloat(String(s || "").replace(/[^0-9.]/g, ""));
    const m = parse(monthly.price_string);
    const y = parse(yearly.price_string);
    if (Number.isNaN(m) || Number.isNaN(y)) return null;
    const savings = m * 12 - y;
    if (savings <= 0) return null;
    return `Save $${savings.toFixed(2)} compared to monthly`;
  }, [sortedIosOfferings]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="bottom"
        className="h-[92vh] bg-white border-none rounded-t-3xl p-0 overflow-hidden max-w-md mx-auto flex flex-col"
        data-testid="paywall-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 bg-white shrink-0">
          <div className="w-12 h-1.5 bg-navy/15 rounded-full mx-auto mb-3" />
          <SheetTitle className="font-jakarta font-extrabold text-3xl text-navy text-center">
            Go <span className="text-orange">Pro</span>
          </SheetTitle>
          <p className="text-sm text-navy-soft font-jakarta font-semibold text-center mt-1">
            {reason
              ? `You've hit the daily ${reasonLabel(reason)} limit on the free plan`
              : "Unlock Pro travel tools for every trip"}
          </p>
        </SheetHeader>

        <div className="overflow-y-auto px-5 pt-1 pb-6 space-y-5">
          {/* Variant B — Early Supporter hero. Web-only. Switches to a
              "Sold Out" card once the launch cap is reached. earlyStatus
              is authoritative for prices (live from /payments/early-supporter/status)
              so a stale `prices` cache can't show the wrong strikethrough.

              App Review lockdown: the Early Supporter tier surfaces the
              "$19.99 / First Year" promo copy Apple flagged. Gate the
              entire hero + sold-out rendering behind `!APP_REVIEW_LOCKDOWN`
              so Terser DCEs the JSX subtree and the copy never lands in
              the production bundle. */}
          {!APP_REVIEW_LOCKDOWN && variant === "B" && !onPro && !ios && earlyStatus && earlyStatus.available && (
            <PaywallEarlySupporterHero
              regularPrice={earlyStatus.regular_price}
              earlyPrice={earlyStatus.early_price}
              busy={busy === "sub_yearly_early"}
              onBuy={() => buy("sub_yearly_early")}
              slotsLeft={earlyStatus.slots_left}
              cap={earlyStatus.cap}
            />
          )}
          {!APP_REVIEW_LOCKDOWN && variant === "B" && !onPro && !ios && earlySoldOut && (
            <PaywallEarlySupporterSoldOut
              onStartPro={() => buy("sub_yearly", {
                paymentLink: prices.find((p) => p.id === "sub_yearly")?.payment_link,
              })}
            />
          )}

          <PaywallIncludes />

          <PaywallTrialBanner user={user} ios={ios} onStartTrial={onStartTrial} />

          {/* Subscriptions */}
          <div>
            <div
              className="text-navy-soft text-[11px] uppercase tracking-[0.2em] font-jakarta font-bold mb-3 flex items-center gap-2 select-none"
            >
              <span className="h-px flex-1 bg-navy/10" />
              Subscriptions
              <span className="h-px flex-1 bg-navy/10" />
            </div>
            <div className="space-y-4">
              {ios && sortedIosOfferings.length === 0 && (
                <>
                  {/* App Review fallback — static cards mirroring the
                      compliant layout so Apple reviewers always see the
                      complete subscription presentation (price + trial +
                      auto-renew terms) even when RevenueCat hasn't returned
                      live packages. Buttons disabled until real packages
                      arrive, per 3.1.2(c) "no purchase without live data". */}
                  <div
                    data-testid="paywall-ios-static-yearly"
                    className="relative rounded-2xl border-2 border-orange bg-white p-4 text-left opacity-90"
                  >
                    <div className="absolute -top-3 left-4 rounded-full bg-orange px-3 py-1">
                      <span className="font-jakarta font-bold text-[10px] uppercase tracking-wider text-white">
                        Best Value
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 mb-1 mt-1">
                      <p className="font-jakarta font-bold text-base text-navy">
                        TipLingo Pro Yearly
                      </p>
                      <p className="font-jakarta font-bold text-base text-navy whitespace-nowrap">
                        $39.99/year
                      </p>
                    </div>
                    <p className="font-jakarta text-xs text-navy-soft mb-1">
                      $39.99/year ($3.33/month) — Save $19.89 vs. monthly
                    </p>
                    <p className="font-jakarta text-[11px] text-navy-soft/80 mb-3">
                      Auto-renews yearly. Cancel anytime in iOS Settings.
                    </p>
                    <button
                      type="button"
                      data-testid="paywall-ios-static-yearly-cta"
                      onClick={() => handleStaticPurchase("tiplingo_pro_yearly", "yearly")}
                      disabled={staticBusy !== null}
                      className="w-full rounded-xl bg-orange text-white font-jakarta font-bold text-sm py-3 active:opacity-80 disabled:opacity-60"
                    >
                      {staticBusy === "yearly" ? "Opening App Store purchase…" : "Subscribe Yearly"}
                    </button>
                  </div>
                  <div
                    data-testid="paywall-ios-static-monthly"
                    className="relative rounded-2xl border border-navy/15 bg-white p-4 text-left opacity-90"
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <p className="font-jakarta font-bold text-base text-navy">
                        TipLingo Pro Monthly
                      </p>
                      <p className="font-jakarta font-bold text-base text-navy whitespace-nowrap">
                        $4.99/month
                      </p>
                    </div>
                    <p className="font-jakarta text-xs text-navy-soft mb-1">
                      $4.99/month
                    </p>
                    <p className="font-jakarta text-[11px] text-navy-soft/80 mb-3">
                      Auto-renews monthly. Cancel anytime in iOS Settings.
                    </p>
                    <button
                      type="button"
                      data-testid="paywall-ios-static-monthly-cta"
                      onClick={() => handleStaticPurchase("tiplingo_pro_monthly", "monthly")}
                      disabled={staticBusy !== null}
                      className="w-full rounded-xl bg-navy text-white font-jakarta font-bold text-sm py-3 active:opacity-80 disabled:opacity-60"
                    >
                      {staticBusy === "monthly" ? "Opening App Store purchase…" : "Subscribe Monthly"}
                    </button>
                  </div>
                  {/* T11 always-visible purchase progress / error strip.
                      Shown automatically the moment a CTA is tapped so the
                      reviewer (and us in TestFlight) can see exactly which
                      step is in flight — Configuring SDK → Fetching Product
                      → Opening Native Sheet. Stays visible after a failure
                      with the exact error so we never hang silently. */}
                  {(staticBusy || purchaseStep || purchaseError) && (
                    <div
                      data-testid="paywall-ios-purchase-progress"
                      className={`rounded-2xl border p-3 text-left ${
                        purchaseError
                          ? "border-red-300 bg-red-50"
                          : "border-navy/15 bg-navy/5"
                      }`}
                    >
                      <p className="font-jakarta font-bold text-[11px] uppercase tracking-wider text-navy-soft mb-1">
                        {purchaseError ? "Purchase Error" : "Purchase Progress"}
                      </p>
                      <p
                        data-testid="paywall-ios-purchase-step"
                        className="font-mono text-[11px] text-navy whitespace-pre-wrap break-words"
                      >
                        {purchaseStep || "—"}
                      </p>
                      {purchaseError && (
                        <button
                          type="button"
                          data-testid="paywall-ios-purchase-error-dismiss"
                          onClick={() => {
                            setPurchaseStep(null);
                            setPurchaseError(null);
                          }}
                          className="mt-2 inline-flex items-center justify-center rounded-lg bg-navy text-white text-[11px] font-jakarta font-bold px-3 py-1.5 active:opacity-80"
                        >
                          Dismiss
                        </button>
                      )}
                    </div>
                  )}
                  {/* T17 / Build 60 — Diagnostic card removed entirely.
                      Previous long-press toggle was an App-Review risk and
                      surfaced stale RC offerings-watchdog text once the
                      RC paywall-open path was decommissioned. */}
                </>
              )}
              {ios
                ? sortedIosOfferings.map((p) => (
                    <PaywallIOSPriceCard
                      key={p.identifier}
                      pkg={p}
                      busy={busy === p.identifier}
                      onBuy={() => buyIOS(p)}
                      isBestValue={p.period === "year"}
                      savingsLabel={
                        p.period === "year" ? iosYearlySavingsLabel : null
                      }
                      flexLabel={
                        p.period === "month" ? "Flexible monthly billing" : null
                      }
                    />
                  ))
                : APP_REVIEW_LOCKDOWN ? (
                  /* Build 84 web lockdown — Option A static safe paywall.
                     Renders synchronously with the initial React tree, so
                     the modal never has an empty subscription slot while
                     backend prices load. No backend copy touches the DOM.
                     Purchases redirect users to the iOS app (Apple IAP is
                     the only approved surface during App Review). */
                  <>
                    <div
                      data-testid="paywall-web-lockdown-yearly"
                      data-tiplingo-safe="1"
                      className="relative rounded-2xl border-2 border-orange bg-white p-4 text-left"
                    >
                      <div className="absolute -top-3 left-4 rounded-full bg-orange px-3 py-1">
                        <span className="font-jakarta font-bold text-[10px] uppercase tracking-wider text-white">
                          Best Value
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between gap-3 mb-1 mt-1">
                        <p className="font-jakarta font-bold text-base text-navy">
                          TipLingo Pro Yearly
                        </p>
                        <p className="font-jakarta font-bold text-base text-navy whitespace-nowrap">
                          $39.99/year
                        </p>
                      </div>
                      <p className="font-jakarta text-[11px] text-navy-soft/80 mb-3">
                        Auto-renews yearly. Cancel anytime.
                      </p>
                      <button
                        type="button"
                        data-testid="paywall-web-lockdown-yearly-cta"
                        disabled
                        className="w-full rounded-xl bg-navy/40 text-white font-jakarta font-bold text-sm py-3 cursor-not-allowed"
                      >
                        Available in the iOS app
                      </button>
                    </div>
                    <div
                      data-testid="paywall-web-lockdown-monthly"
                      data-tiplingo-safe="1"
                      className="relative rounded-2xl border border-navy/15 bg-white p-4 text-left"
                    >
                      <div className="flex items-baseline justify-between gap-3 mb-1">
                        <p className="font-jakarta font-bold text-base text-navy">
                          TipLingo Pro Monthly
                        </p>
                        <p className="font-jakarta font-bold text-base text-navy whitespace-nowrap">
                          $4.99/month
                        </p>
                      </div>
                      <p className="font-jakarta text-[11px] text-navy-soft/80 mb-3">
                        Auto-renews monthly. Cancel anytime.
                      </p>
                      <button
                        type="button"
                        data-testid="paywall-web-lockdown-monthly-cta"
                        disabled
                        className="w-full rounded-xl bg-navy/40 text-white font-jakarta font-bold text-sm py-3 cursor-not-allowed"
                      >
                        Available in the iOS app
                      </button>
                    </div>
                    <p
                      data-testid="paywall-web-lockdown-note"
                      data-tiplingo-safe="1"
                      className="font-jakarta font-medium text-xs text-navy-soft italic text-center"
                    >
                      Subscriptions are available in the TipLingo iOS app
                      through Apple In-App Purchase.
                    </p>
                  </>
                ) : subs.map((p) => {
                    // Compute "Save $X vs monthly" badge for yearly SKUs so
                    // users see the concrete dollar value of switching to
                    // yearly (vs paying $4.99 × 12 = $59.88).
                    const monthly = subsRaw.find((x) => x.id === "sub_monthly")?.amount;
                    let savingsBadge = null;
                    let displayPkg = p;
                    if (
                      p.id === "sub_yearly" &&
                      monthly &&
                      p.amount < monthly * 12
                    ) {
                      const save = monthly * 12 - p.amount;
                      savingsBadge = `Save $${save.toFixed(2)} vs monthly`;
                    } else if (p.id === "sub_yearly_early") {
                      // Match the hero copy: first-year discount, then
                      // auto-renews at regular yearly price. The card
                      // already shows the renews_at sub-line via the
                      // pkg.renews_at hint from the backend.
                      //
                      // App Review lockdown: this block sets the badge
                      // + label to "First year only" / "TipLingo Pro
                      // First Year" — copy Apple flagged. Under
                      // lockdown the block is a no-op and Terser DCEs
                      // both string literals from the production
                      // bundle. Restored automatically post-approval.
                      if (!APP_REVIEW_LOCKDOWN) {
                        savingsBadge = "First year only";
                        displayPkg = {
                          ...p,
                          label: "TipLingo Pro First Year",
                        };
                      }
                    }
                    return (
                      <PaywallPriceCard
                        key={p.id}
                        pkg={displayPkg}
                        highlight={p.id === "sub_yearly_early"}
                        badge={savingsBadge}
                        iconImg="/assets/lightning-bolt.png"
                        iconImgClassName="w-16 h-16"
                        busy={busy === p.id}
                        onBuy={() => buy(p.id)}
                      />
                    );
                  })}
            </div>
          </div>

          {/* Credit packs — web only. Apple disallows non-IAP payment surfaces.
              App Review lockdown: the entire block (including the visible
              "No-subscription credit packs" heading and the credit_100 /
              credit_300 / credit_1000 price cards) is stripped from the
              production bundle. Restored post-approval by flipping
              APP_REVIEW_LOCKDOWN. */}
          {!APP_REVIEW_LOCKDOWN && !ios && (
            <div>
              <div className="text-navy-soft text-[11px] uppercase tracking-[0.2em] font-jakarta font-bold mb-3 flex items-center gap-2">
                <span className="h-px flex-1 bg-navy/10" />
                No-subscription credit packs
                <span className="h-px flex-1 bg-navy/10" />
              </div>
              <p className="font-jakarta font-medium text-xs text-navy-soft italic mb-3">
                Credits kick in only after your daily free limit is used up.
              </p>
              <div className="space-y-2.5">
                {credits.map((p) => {
                  // Realistic 3D coin imagery scales with the pack size so
                  // the visual weight matches the value. The 1,000 pack
                  // uses a bigger render so the double-stack reads clearly.
                  const iconForId = {
                    credits_100: "/assets/credits-100.png",
                    credits_300: "/assets/credits-300.png",
                    credits_1000: "/assets/credits-1000.png",
                  }[p.id];
                  const big = p.id === "credits_1000";
                  return (
                    <PaywallPriceCard
                      key={p.id}
                      pkg={p}
                      iconImg={iconForId}
                      iconImgClassName={big ? "w-16 h-16" : "w-12 h-12"}
                      busy={busy === p.id}
                      onBuy={() => buy(p.id)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Restore Purchases is now wired into the iOS disclosure footer
              below (V2 design — single inline link with Terms + Privacy).
              On web the customer portal handles restore. */}

          {ios ? (
            <PaywallIOSDisclosure onRestore={onRestore} />
          ) : APP_REVIEW_LOCKDOWN ? (
            <p className="text-[11px] text-navy-soft font-jakarta font-medium italic text-center">
              Cancel anytime · manage from your account
            </p>
          ) : (
            <p className="text-[11px] text-navy-soft font-jakarta font-medium italic text-center">
              Test mode · powered by Stripe · cancel anytime
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function reasonLabel(k) {
  if (k === "translate_text") return "text translation";
  if (k === "translate_voice") return "voice translation";
  if (k === "receipt") return "receipt scan";
  if (k === "menu") return "menu scan";
  return k;
}
